import { PageHeader } from "@bednbody/ui/components/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@bednbody/ui/components/tabs";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { isHubScoped } from "@/lib/jwt-claims";
import { useHubsAdmin } from "../hubs/hubs.hooks";
import { BatchesPanel } from "./components/BatchesPanel";
import { LiveSessionsPanel } from "./components/LiveSessionsPanel";
import { OutcomesPanel } from "./components/OutcomesPanel";

/** Today in Manila — the service date a Session belongs to. */
const todayInManila = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Manila" }).format(new Date());

/**
 * Rider Sessions — supply, what is running, and what it came to.
 *
 * Three surfaces on one page rather than three routes, because they are three
 * views of the same object at three points in its life and an operator moves
 * between them constantly. Rank lives on the Roster instead (RIDER-031) — it is
 * a property of a rider, not of a Session.
 */
export function RiderSessionsPage() {
  const { claims } = useAuth();
  const role = claims?.role;
  const hubScoped = role ? isHubScoped(role) : false;
  const hubId = hubScoped ? (claims?.hub_id ?? null) : null;
  const { data: hubs } = useHubsAdmin();
  const [serviceDate, setServiceDate] = useState(todayInManila);

  // §2: supply is generated by the approved calculation, and Hub Manager is not
  // on the endpoint's role list — showing them the control would offer an action
  // the server refuses.
  const canGenerate = role === "admin" || role === "superadmin";
  // OQ-122 gave Session overrides and manual Sessions to Admin; the admin Edge
  // function's `sessionOverrideGate` is admin + superadmin, and Hub Manager is
  // deliberately not on it. Showing them the controls only let them fill in a
  // form the server was certain to refuse.
  const canManage = role === "admin" || role === "superadmin";
  // Rider pay (earnings, guarantee decisions) is withheld from hub_manager by
  // grant (0064, 0067) — asking for it failed the whole read.
  const canSeePay = !hubScoped;
  const canReview = role === "superadmin";
  // 0245 — narrower than canManage on purpose: retiming is Superadmin only,
  // the route 403s Admin. Showing the option to a plain Admin would offer an
  // action the server is certain to refuse.
  const canRetime = role === "superadmin";

  return (
    <div className="min-w-0 space-y-8">
      <PageHeader
        description={
          hubScoped
            ? "Your hub's generated supply, live Sessions and finished outcomes."
            : "Generated supply, live Sessions and finished outcomes across all hubs."
        }
        title="Rider Sessions"
      />

      <Tabs defaultValue="live">
        <TabsList
          className="w-full min-w-0 max-w-full justify-start overflow-x-auto border-b border-border mb-4"
          variant="line"
        >
          <TabsTrigger className="flex-none px-3" value="live">
            Live
          </TabsTrigger>
          <TabsTrigger className="flex-none px-3" value="batches">
            Supply
          </TabsTrigger>
          <TabsTrigger className="flex-none px-3" value="outcomes">
            Outcomes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="live">
          <LiveSessionsPanel
            hubId={hubId}
            hubs={(hubs ?? []).map((hub) => ({ id: hub.id, name: hub.name }))}
            canManage={canManage}
            canRetime={canRetime}
            serviceDate={serviceDate}
            onServiceDateChange={setServiceDate}
          />
        </TabsContent>

        <TabsContent value="batches">
          <BatchesPanel
            hubId={hubId}
            hubs={(hubs ?? []).map((hub) => ({ id: hub.id, name: hub.name }))}
            canGenerate={canGenerate}
          />
        </TabsContent>

        <TabsContent value="outcomes">
          <OutcomesPanel hubId={hubId} canSeePay={canSeePay} canReview={canReview} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
